import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { Order, CategoryType } from "../../../services/Order Management";
import orderAdapter from "src/adapters/order";
import { select as select_characteristic_values } from "../characteristic_value";

const orderService = new Order();
const orderCategoryType = new CategoryType();

const REDUCER_MODULE_NAME = "ORDER";

const initialState = {
  grid: {
    orders: {
      page: 1,
      skip: 0,
      take: 6,
      rows: 0,
      data: [],
      show: 6,
      error: false,
      loading: true,
      category: null,
      filters: {},
      requestId: null,
      pendingRequest: null,
      failedRequest: null,
    },
    activos: {
      page: 1,
      skip: 0,
      take: 3,
      rows: 0,
      data: [],
      show: 6,
      error: false,
      loading: true,
      category: null,
      filters: {},
      requestId: null,
      pendingRequest: null,
      failedRequest: null,
    },
    inactivos: {
      page: 1,
      skip: 0,
      take: 3,
      rows: 0,
      data: [],
      show: 6,
      error: false,
      loading: true,
      category: null,
      filters: {},
      requestId: null,
      pendingRequest: null,
      failedRequest: null,
    },
  },
  loading: true,
  formIsReady: false,
  date: "",
  categorySelected: {},
  data: "",
  detailRequestId: null,
  message: "",
  error: "",
  select: [],
};

const loadingReducer = (state, _) => {
  return {
    ...state,
    formIsReady: false,
    loading: true,
  };
};

export const toggle_loading = createAsyncThunk(
  `TOGGLE_LOADING_${REDUCER_MODULE_NAME}`,
  async ({ status }) => {
    return {
      loading: status,
    };
  }
);

export const grid_activos = createAsyncThunk(
  `GRID_ACTIVOS_${REDUCER_MODULE_NAME}`,
  async ({ skip, take, currentPage = 1, category, filters = {} }) => {
    const { data } = await orderService.gridByActive(
      skip,
      take,
      1,
      category,
      filters
    );

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  }
);

export const grid_inactivos = createAsyncThunk(
  `GRID_INACTIVOS_${REDUCER_MODULE_NAME}`,
  async ({ skip, take, currentPage = 1, category, filters = {} }) => {
    const { data } = await orderService.gridByActive(
      skip,
      take,
      0,
      category,
      filters
    );

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  }
);

export const grid_orders = createAsyncThunk(
  `GRID_${REDUCER_MODULE_NAME}`,
  async ({ skip, take, currentPage = 1, category, filters = {} }) => {
    const { data } = await orderService.grid(skip, take, category, filters);

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  }
);

export const getById = createAsyncThunk(
  `GET_${REDUCER_MODULE_NAME}`,
  async (id) => {
    const order = await orderService.getById(id);

    return {
      order: order.data,
    };
  }
);

export const changeActive = createAsyncThunk(
  `CHANGE_ACTIVE_${REDUCER_MODULE_NAME}`,
  async ({ id }, { dispatch }) => {
    await orderService.changeActive(id);

    dispatch(
      grid_activos({
        skip: initialState.grid.activos.skip,
        take: initialState.grid.activos.take,
      })
    );
    dispatch(
      grid_inactivos({
        skip: initialState.grid.inactivos.skip,
        take: initialState.grid.inactivos.take,
      })
    );
  }
);

export const form = createAsyncThunk(
  `FORM_${REDUCER_MODULE_NAME}`,
  async ({ id, category }, { dispatch }) => {
    const [{ data: date }, { data: categories }] = await Promise.all([
      orderService.today(),
      orderCategoryType.select(),
    ]);

    const categoryToUse = categories.find((c) =>
      category.includes(c.value.toLowerCase())
    );

    if (id) {
      dispatch(getById(id));
    }

    return {
      date,
      categorySelected: categoryToUse,
    };
  }
);

export const check_characteristic_values_select = createAsyncThunk(
  `CHECK_CHARACTERISTIC_VALUES_SELECT_${REDUCER_MODULE_NAME}`,
  async ({ characteristics, position }, { dispatch }) => {
    dispatch(
      select_characteristic_values({
        base_url: orderService.base_url,
        characteristic_type: characteristics[position].characteristic_type_id,
      })
    );
  }
);

export const submit = createAsyncThunk(
  `SUBIMIT_FORM_${REDUCER_MODULE_NAME}`,
  async ({ id, data }, { getState, rejectWithValue }) => {
    const { characteristic_type, characteristic_values } = getState();

    const characteristics = data.characteristics.map(
      ({ characteristic_type_id, value }) => {
        const characteristic_type_complete = characteristic_type.list.find(
          (item) => item.id === characteristic_type_id
        );

        const characteristic_value_complete = (
          characteristic_values.select?.find(
            (item) => String(item.characteristic_type) === String(characteristic_type_id),
          )?.values || []
        ).find((characteristic) => String(characteristic.value) === String(value));

        if (!characteristic_value_complete)
          return { characteristic_type_id, value };

        if (characteristic_type_complete.input_type === "SELECT")
          return {
            characteristic_type_id,
            value: characteristic_value_complete.label,
          };

        return { characteristic_type_id, value };
      }
    );

    const data_to_submit = orderAdapter.from_form_to_api({
      ...data,
      characteristics,
    });

    try {
      if (id) {
        await orderService.update(id, data_to_submit);
      } else {
        await orderService.create(data_to_submit);
      }

      return {
        message: `Orden ${id ? "modificada" : "creada"} correctamente.`,
        error: "",
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.errors ||
          error?.message ||
          "No pudimos guardar la orden. Intentá nuevamente.",
      );
    }
  }
);

export const orderSlice = createSlice({
  name: REDUCER_MODULE_NAME,
  initialState,
  reducers: {
    clean() {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: {
    [grid_orders.pending.type]: (state, action) => {
      const filters = action.meta.arg.filters || {};
      const categoryChanged =
        state.grid.orders.category !== action.meta.arg.category;
      const filtersChanged =
        JSON.stringify(state.grid.orders.filters) !== JSON.stringify(filters);

      return {
        ...state,
        grid: {
          ...state.grid,
          orders: {
            ...state.grid.orders,
            ...(categoryChanged || filtersChanged
              ? { data: [], rows: 0, page: 1, skip: 0 }
              : {}),
            error: false,
            loading: true,
            category: action.meta.arg.category,
            filters,
            requestId: action.meta.requestId,
            pendingRequest: action.meta.arg,
            failedRequest: null,
          },
        },
      };
    },
    [grid_activos.pending.type]: (state, action) => {
      const categoryChanged =
        state.grid.activos.category !== action.meta.arg.category;
      const filters = action.meta.arg.filters || {};
      const filtersChanged =
        JSON.stringify(state.grid.activos.filters) !== JSON.stringify(filters);

      return {
        ...state,
        loading: true,
        grid: {
          ...state.grid,
          activos: {
            ...state.grid.activos,
            ...(categoryChanged || filtersChanged
              ? { data: [], rows: 0, page: 1, skip: 0 }
              : {}),
            error: false,
            loading: true,
            category: action.meta.arg.category,
            filters,
            requestId: action.meta.requestId,
            pendingRequest: action.meta.arg,
            failedRequest: null,
          },
        },
      };
    },
    [grid_inactivos.pending.type]: (state, action) => {
      const categoryChanged =
        state.grid.inactivos.category !== action.meta.arg.category;
      const filters = action.meta.arg.filters || {};
      const filtersChanged =
        JSON.stringify(state.grid.inactivos.filters) !==
        JSON.stringify(filters);

      return {
        ...state,
        loading: true,
        grid: {
          ...state.grid,
          inactivos: {
            ...state.grid.inactivos,
            ...(categoryChanged || filtersChanged
              ? { data: [], rows: 0, page: 1, skip: 0 }
              : {}),
            error: false,
            loading: true,
            category: action.meta.arg.category,
            filters,
            requestId: action.meta.requestId,
            pendingRequest: action.meta.arg,
            failedRequest: null,
          },
        },
      };
    },
    [getById.pending.type]: (state, action) => {
      return {
        ...state,
        formIsReady: false,
        loading: true,
        error: "",
        data: "",
        detailRequestId: action.meta.requestId,
      };
    },
    [form.pending.type]: loadingReducer,
    [submit.pending.type]: loadingReducer,
    [toggle_loading.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: action.payload.loading,
      };
    },
    [grid_orders.fulfilled.type]: (state, action) => {
      if (state.grid.orders.requestId !== action.meta.requestId) return state;

      return {
        ...state,
        grid: {
          ...state.grid,
          orders: {
            ...state.grid.orders,
            page: action.payload.page,
            skip: action.payload.skip,
            take: action.payload.take,
            rows: action.payload.grid_data.rows,
            data: action.payload.grid_data.data,
            error: false,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: null,
          },
        },
      };
    },
    [grid_activos.fulfilled.type]: (state, action) => {
      if (state.grid.activos.requestId !== action.meta.requestId) return state;

      return {
        ...state,
        loading: false,
        error: "",
        grid: {
          ...state.grid,
          activos: {
            ...state.grid.activos,
            page: action.payload.page,
            skip: action.payload.skip,
            take: action.payload.take,
            rows: action.payload.grid_data.rows,
            data: action.payload.grid_data.data,
            error: false,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: null,
          },
        },
      };
    },
    [grid_inactivos.fulfilled.type]: (state, action) => {
      if (state.grid.inactivos.requestId !== action.meta.requestId)
        return state;

      return {
        ...state,
        loading: false,
        error: "",
        grid: {
          ...state.grid,
          inactivos: {
            ...state.grid.inactivos,
            page: action.payload.page,
            skip: action.payload.skip,
            take: action.payload.take,
            rows: action.payload.grid_data.rows,
            data: action.payload.grid_data.data,
            error: false,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: null,
          },
        },
      };
    },
    [grid_activos.rejected.type]: (state, action) => {
      if (state.grid.activos.requestId !== action.meta.requestId) return state;

      return {
        ...state,
        loading: false,
        grid: {
          ...state.grid,
          activos: {
            ...state.grid.activos,
            error: true,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: action.meta.arg,
          },
        },
      };
    },
    [grid_inactivos.rejected.type]: (state, action) => {
      if (state.grid.inactivos.requestId !== action.meta.requestId)
        return state;

      return {
        ...state,
        loading: false,
        grid: {
          ...state.grid,
          inactivos: {
            ...state.grid.inactivos,
            error: true,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: action.meta.arg,
          },
        },
      };
    },
    [grid_orders.rejected.type]: (state, action) => {
      if (state.grid.orders.requestId !== action.meta.requestId) return state;

      return {
        ...state,
        grid: {
          ...state.grid,
          orders: {
            ...state.grid.orders,
            error: true,
            loading: false,
            requestId: null,
            pendingRequest: null,
            failedRequest: action.meta.arg,
          },
        },
      };
    },
    [getById.fulfilled.type]: (state, action) => {
      if (state.detailRequestId !== action.meta.requestId) return state;

      return {
        ...state,
        loading: false,
        error: "",
        data: action?.payload?.order || "",
        detailRequestId: null,
      };
    },
    [getById.rejected.type]: (state, action) => {
      if (state.detailRequestId !== action.meta.requestId) return state;

      return {
        ...state,
        loading: false,
        error:
          action?.error?.message || "No pudimos cargar el detalle de la orden",
        data: "",
        detailRequestId: null,
      };
    },
    [form.fulfilled.type]: (state, action) => {
      return {
        ...state,
        message: "",
        loading: false,
        formIsReady: false,
        date: action.payload.date,
        categorySelected: action.payload.categorySelected,
      };
    },
    [submit.fulfilled.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        message: action.payload.message,
        formIsReady: !!action.payload.message,
        error: action.payload.error,
      };
    },
    [submit.rejected.type]: (state, action) => {
      return {
        ...state,
        loading: false,
        formIsReady: false,
        message: "",
        error: action.payload || "No pudimos guardar la orden. Intentá nuevamente.",
      };
    },
  },
});

export const { clean } = orderSlice.actions;

export default orderSlice.reducer;
